import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileText, Image, Calendar, DollarSign,
  Users, FolderOpen, ClipboardList, LogOut, Menu, X, ExternalLink, Star, HelpCircle
} from "lucide-react";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/inscripciones", icon: ClipboardList, label: "Inscripciones" },
  { to: "/admin/formulario", icon: FileText, label: "Formulario" },
  { to: "/admin/contenido", icon: LayoutDashboard, label: "Contenido Hero" },
  { to: "/admin/actividades", icon: Image, label: "Actividades" },
  { to: "/admin/horarios", icon: Calendar, label: "Horarios" },
  { to: "/admin/precios", icon: DollarSign, label: "Precios" },
  { to: "/admin/monitores", icon: Users, label: "Monitores" },
  { to: "/admin/resenas", icon: Star, label: "Reseñas" },
  { to: "/admin/faqs", icon: HelpCircle, label: "FAQs" },
  { to: "/admin/galeria", icon: FolderOpen, label: "Galería" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-6 py-5 border-b border-[#E2E8F0]">
        <div className="font-['Montserrat'] font-bold text-[#0072A4] text-base leading-tight">Campus Verano</div>
        <div className="text-[#94A3B8] text-xs font-['Montserrat']">Panel de Administración</div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg mb-1 font-['Montserrat'] text-sm font-semibold transition-all ${
                isActive
                  ? "bg-[#0072A4] text-white shadow-sm"
                  : "text-[#475569] hover:bg-[#F0F8FF] hover:text-[#0072A4]"
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-[#E2E8F0]">
        <div className="text-xs text-[#94A3B8] font-['Montserrat'] mb-3 px-2">
          Sesión: <span className="text-[#475569]">{user?.email}</span>
        </div>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-[#475569] hover:text-[#0072A4] hover:bg-[#F0F8FF] transition-all font-['Montserrat'] text-sm mb-1"
          data-testid="view-site-link"
        >
          <ExternalLink size={16} /> Ver sitio web
        </a>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-red-500 hover:bg-red-50 transition-all font-['Montserrat'] text-sm font-semibold"
        >
          <LogOut size={16} /> Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex admin-font">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-[#E2E8F0] flex-col flex-shrink-0 fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="bg-white w-64 border-r border-[#E2E8F0] flex flex-col">
            <div className="flex justify-end p-4">
              <button onClick={() => setSidebarOpen(false)} className="p-1 rounded-lg hover:bg-[#F0F8FF]">
                <X size={20} color="#475569" />
              </button>
            </div>
            <SidebarContent />
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white border-b border-[#E2E8F0] px-4 sm:px-6 py-3 flex items-center gap-4 sticky top-0 z-20">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-[#F0F8FF]"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-sidebar-toggle"
          >
            <Menu size={20} color="#475569" />
          </button>
          <div className="flex-1" />
          <span className="font-['Montserrat'] text-sm text-[#475569]">{user?.name || "Admin"}</span>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
